import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { SUPPORTED_LANGUAGES, shouldShowLanguageSwitcher } from '../utils/languageSwitcher';
import useFooterDock from '../hooks/useFooterDock';
import './LanguageSwitcher.css';

const STORAGE_KEY = 'integritos-language-preference';
const GOOGLE_TRANSLATE_SCRIPT_ID = 'integritos-google-translate-script';
const GOOGLE_TRANSLATE_CALLBACK = 'integritosGoogleTranslateInit';
const SOURCE_LANGUAGE = 'en';

const getSavedLanguage = () => {
  if (typeof window === 'undefined') return SOURCE_LANGUAGE;
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return SUPPORTED_LANGUAGES.some(({ code }) => code === saved) ? saved : SOURCE_LANGUAGE;
};

const setTranslationCookie = (languageCode) => {
  const cookieOptions = 'path=/; SameSite=Lax';

  if (languageCode === SOURCE_LANGUAGE) {
    document.cookie = `googtrans=; Max-Age=0; ${cookieOptions}`;
    document.cookie = `googtrans=/en/en; ${cookieOptions}`;
    return;
  }

  document.cookie = `googtrans=/en/${languageCode}; ${cookieOptions}`;
};

export default function LanguageSwitcher() {
  const { pathname } = useLocation();
  const footerDock = useFooterDock('site-footer-language', pathname);
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState(getSavedLanguage);
  const controlRef = useRef(null);

  useEffect(() => {
    if (!shouldShowLanguageSwitcher(pathname)) return undefined;
    // The build-time renderer runs on this dedicated local port. Keep its
    // crawler snapshots free of transient third-party translation assets.
    if (window.location.hostname === 'localhost' && window.location.port === '4567') return undefined;

    const initializeTranslate = () => {
      const host = document.getElementById('google_translate_element');
      if (!host || host.childElementCount > 0 || !window.google?.translate?.TranslateElement) return;

      new window.google.translate.TranslateElement({
        pageLanguage: SOURCE_LANGUAGE,
        includedLanguages: SUPPORTED_LANGUAGES.map(({ code }) => code).join(','),
        autoDisplay: false
      }, 'google_translate_element');
    };

    window[GOOGLE_TRANSLATE_CALLBACK] = initializeTranslate;

    if (window.google?.translate?.TranslateElement) {
      initializeTranslate();
      return undefined;
    }

    if (!document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
      script.src = `https://translate.google.com/translate_a/element.js?cb=${GOOGLE_TRANSLATE_CALLBACK}`;
      script.async = true;
      document.body.appendChild(script);
    }

    return undefined;
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!controlRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  if (!shouldShowLanguageSwitcher(pathname)) return null;

  const selectLanguage = (languageCode) => {
    if (languageCode === language) {
      setIsOpen(false);
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, languageCode);
    setTranslationCookie(languageCode);
    setLanguage(languageCode);
    setIsOpen(false);
    window.location.reload();
  };

  const control = (
    <div className="language-switcher notranslate" ref={controlRef} translate="no">
      {isOpen && (
        <div className="language-switcher-menu" id="language-switcher-menu" role="menu" aria-label="Choose a language">
          {SUPPORTED_LANGUAGES.map((option) => (
            <button
              type="button"
              role="menuitemradio"
              aria-checked={language === option.code}
              className={`language-switcher-option ${language === option.code ? 'is-active' : ''}`}
              key={option.code}
              onClick={() => selectLanguage(option.code)}
            >
              <span className="language-switcher-flag" aria-hidden="true">{option.flag}</span>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        className="language-switcher-trigger"
        aria-expanded={isOpen}
        aria-controls="language-switcher-menu"
        onClick={() => setIsOpen((current) => !current)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.4 2.5 3.6 5.5 3.6 9S14.4 18.5 12 21c-2.4-2.5-3.6-5.5-3.6-9S9.6 5.5 12 3Z" />
        </svg>
        <span>Languages</span>
        <svg className="language-switcher-chevron" viewBox="0 0 12 8" aria-hidden="true">
          <path d="m1 1 5 5 5-5" />
        </svg>
      </button>
    </div>
  );
  return (
    <>
      <div id="google_translate_element" aria-hidden="true" />
      {footerDock ? createPortal(control, footerDock) : control}
    </>
  );
}
